import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, LinearProgress } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { TEST, VIDEO } from '../../../utils/constants'
import { H3 } from '../../../layouts/TextStyles'
import { capitalize } from '../../../utils/functions'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import { topicApiSlice } from '../../../features/topic/topicSlice'
import Breadcrumb from '../../../components/Breadcrumb'
import { SectionState, UserTopicState } from '../../../features/types'
import Topic from './components/Topic'
import VideoContent from './components/VideoContent'
import QuizContent from './components/QuizContent'
import LessonLinksDialog from './components/LessonLinksDialog'

const styles = {
    flexCenter: {
        display: 'grid',
        gridTemplateColumns: '45% 55%',
        alignItems: 'start',
        justifyContent: 'space-between',
        gap: '24px',
    },
    content: {
        width: '100%',
        borderRadius: '8px',
    },
    buttons: {
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'space-between',
        mt: 4,
    },
    aside: {
        maxHeight: 750,
    },
}

const SubjectPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const { courseSubjectId } = useParams()
    const [selectedSection, setSelectedSection] = useState(null)
    const [selectedTopic, setSelectedTopic] = useState(null)
    const { data: topics = [], isFetching: isFetchingTopics } =
        topicApiSlice.useGetTeacherTopicsBySubjectQuery(+courseSubjectId)

    const {
        data: sectionDetails = null,
        isFetching: isFetchingSectionDetails,
    } = sectionApiSlice.useGetSectionDetailsQuery(
        {
            ...selectedSection,
        },
        {
            skip: !selectedSection,
        },
    )

    const { data: selectedTopicSections = [], isFetching: isFetchingSections } =
        sectionApiSlice.useGetTopicSectionsQuery(selectedTopic?.courseTopicId, {
            skip: !selectedTopic,
        })

    const currentSectionIndex = selectedTopicSections.findIndex(
        (el) => el.id === selectedSection.id,
    )

    const onNextSection = () => {
        const isLastSection =
            currentSectionIndex === selectedTopicSections.length - 1

        if (currentSectionIndex > -1 && !isLastSection) {
            const nextSection = selectedTopicSections[currentSectionIndex + 1]
            setSelectedSection(nextSection)
        }
    }

    const onPreviousSection = () => {
        const isFirstSection = currentSectionIndex === 0

        if (currentSectionIndex > 0 && !isFirstSection) {
            const previousSection =
                selectedTopicSections[currentSectionIndex - 1]
            setSelectedSection(previousSection)
        }
    }

    const handleSelect = (topic: UserTopicState, section: SectionState) => {
        setSelectedSection(section)
        setSelectedTopic(topic)
    }

    const getBreadcrumb = () => {
        const courseTitle = state?.course
            ? state?.course?.[`title${capitalize(locale)}`]
            : ''
        const subjectTitle = state?.subject
            ? state?.subject?.[`title${capitalize(locale)}`]
            : ''

        return (
            <Breadcrumb
                links={[
                    formatMessage({ id: 'myCourses' }),
                    courseTitle,
                    subjectTitle,
                ]}
            />
        )
    }

    return (
        <Wrapper>
            {getBreadcrumb()}

            <Content>
                <Header>
                    <Title>
                        <H3>
                            {state?.subject &&
                                state.subject?.[`title${capitalize(locale)}`] +
                                    ' ' +
                                    formatMessage({
                                        id: 'topics',
                                    }).toLowerCase()}
                        </H3>
                    </Title>

                    <LessonLinksDialog courseSubjectId={+courseSubjectId} />
                </Header>

                {isFetchingTopics && <LinearProgress />}

                <Box sx={styles.flexCenter}>
                    <Box sx={styles.aside}>
                        {topics.map((topic, index) => (
                            <Topic
                                key={index}
                                topic={topic}
                                selectedSection={selectedSection}
                                handleSelect={handleSelect}
                            />
                        ))}
                    </Box>

                    <Box sx={styles.content}>
                        {(isFetchingSectionDetails || isFetchingSections) && (
                            <LinearProgress />
                        )}

                        {sectionDetails && (
                            <Box>
                                {selectedSection.type === VIDEO && (
                                    <VideoContent
                                        videoDetails={sectionDetails}
                                        videoLessonId={selectedSection.id}
                                    />
                                )}

                                {selectedSection.type === TEST && (
                                    <QuizContent
                                        section={selectedSection}
                                        sectionDetails={sectionDetails}
                                        topic={null}
                                    />
                                )}

                                <Box sx={styles.buttons}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ChevronLeft />}
                                        onClick={() => onPreviousSection()}
                                        disabled={currentSectionIndex === 0}
                                    >
                                        {formatMessage({ id: 'back' })}
                                    </Button>

                                    <Button
                                        variant="contained"
                                        endIcon={<ChevronRight />}
                                        onClick={() => onNextSection()}
                                        disabled={
                                            currentSectionIndex ===
                                            selectedTopicSections.length - 1
                                        }
                                    >
                                        {formatMessage({ id: 'next' })}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Content>
        </Wrapper>
    )
}

export default SubjectPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px 40px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
